import React from 'react';
import { useFormikContext } from 'formik';
import { FormFeedback } from 'Components/frameworks/form-feedback/FormFeedback';
import { Title } from 'Components/frameworks/title/Title';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { getCGVFromLegalInfos } from 'Components/utils/booking/bookingUtils';
import { HIPAY_PSP_ID } from 'Components/widgets/payment-info/PaymentInfo.const';
import { t } from 'Src/i18n';
import * as yup from 'yup';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import { FormikInput } from 'Components/frameworks/formik/FormikInput';
type Props = {
  i18n: I18N;
  shouldDisplayDonationTerms: boolean;
  shouldSubmitFormOnAccept: boolean;
  isDisabled: boolean;
  hasNoPaymentMethods?: boolean;
};
export const TermsAndConditions = ({
  i18n,
  shouldDisplayDonationTerms,
  shouldSubmitFormOnAccept,
  isDisabled,
  hasNoPaymentMethods = false,
}: Props) => {
  const formik = useFormikContext();
  const legalMessage = getCGVFromLegalInfos(i18n.bookingProcess);

  const submitFormOnAccept = ({ currentTarget }) => {
    const shouldProceedToSubmit =
      currentTarget.checked &&
      (!shouldDisplayDonationTerms ||
        formik.values.termsAndConditions.donation ||
        formik.values.termsAndConditions.cgv);

    if (shouldSubmitFormOnAccept && shouldProceedToSubmit && formik.values.paymentMethodId) {
      // submit form if asked and all the cgv are checked and a paymentMethodId is selected
      // should wait for validation to be done
      setTimeout(formik.submitForm, 0);
    }

    if (
      hasNoPaymentMethods &&
      shouldProceedToSubmit &&
      formik.values.paymentProviderId === HIPAY_PSP_ID
    ) {
      setTimeout(() => {
        scrollToElement('#payment-form-iframe');
      }, 500);
    }
  };

  const hasCGVOrDonationError =
    (formik.errors?.termsAndConditions?.cgv && formik.touched?.termsAndConditions?.cgv) ||
    (formik.errors?.termsAndConditions?.donation && formik.touched?.termsAndConditions?.donation);
  const checkboxProps = {
    isDisabled,
    isRequired: true,
    onChange: submitFormOnAccept,
    type: 'checkbox',
    validate: yup.boolean().oneOf([true]),
    ariaDescribedBy: 'termsAndConditions-cgv-error',
  };
  return (
    <div data-testid="CGV-block">
      <Title className="mb-10" id="termsAndConditionsTitleId" theme="title-brand-n2">
        {legalMessage.label}
      </Title>
      <Capsule header="none" contentClassName="p-20">
        <FormikInput
          {...checkboxProps}
          label={
            <React.Fragment>
              <span>{legalMessage.description}</span>
              <br />
              <span>
                {t('booking.banking.termsAndConditions.acceptCGV')}
                &nbsp;
                <a
                  className="text-inherit underline"
                  href={legalMessage.url}
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  {t('booking.banking.termsAndConditions.termsOfSales')}
                </a>
              </span>
            </React.Fragment>
          }
          name="termsAndConditions.cgv"
        />
        {shouldDisplayDonationTerms && (
          <div className="mt-20">
            <FormikInput
              {...checkboxProps}
              label={
                <React.Fragment>
                  {t('booking.banking.donation.acceptCGU')}&nbsp;
                  <a
                    className="text-inherit underline"
                    href={t('booking.banking.donation.linkDonationTerms')}
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    {t('booking.banking.donation.donationTerms')}
                  </a>
                </React.Fragment>
              }
              name="termsAndConditions.donation"
            />
          </div>
        )}
      </Capsule>

      {hasCGVOrDonationError && (
        <div aria-live="polite" id="termsAndConditions-cgv-error">
          <FormFeedback isError message={t('booking.banking.termsAndConditions.errorMessage')} />
        </div>
      )}
    </div>
  );
};
